import React, { useEffect, useState } from "react";
import { Search, MapPin, X, Globe, ExternalLink, Ticket, Copy, Check, Lock } from "lucide-react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";

const PLACEHOLDER_LOGO = "https://via.placeholder.com/120x80?text=Logo";
const PLACEHOLDER_BANNER = "linear-gradient(135deg, #12873B 0%, #2D302D 100%)";

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null); 
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all"); 
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [claimedCode, setClaimedCode] = useState(null); 
  const [copied, setCopied] = useState(null); 
  const [hasCard, setHasCard] = useState(null);

  useEffect(() => {
    const navElement = document.querySelector("nav");
    if (navElement) {
      navElement.style.display = selectedPartner ? "none" : "flex";
    }
    return () => { if (navElement) navElement.style.display = "flex"; };
  }, [selectedPartner]);

  useEffect(() => {
    const loadPartnersAndStatus = async () => {
      setLoading(true);
      const { data: partnersData } = await supabase
        .from("partners")
        .select("*")
        .order("name", { ascending: true });
      setPartners(partnersData || []);

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("has_card")
          .eq("user_id", session.user.id)
          .single();
        if (profile) setHasCard(profile.has_card);
      }
      setLoading(false);
    };
    loadPartnersAndStatus();
  }, []);

  const handleRevealCode = async () => {
    if (selectedPartner.name !== "RSM Store") {
      setShowCode(true);
      return;
    }

    setClaiming(true);
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      alert("Please log in to claim a code.");
      setClaiming(false);
      return;
    }

    const { data: existing } = await supabase
      .from("partner_codes")
      .select("code")
      .eq("brand", "RSM Store")
      .eq("campaign", "forever")
      .eq("assigned_to", session.user.id)
      .maybeSingle();

    if (existing) {
      setClaimedCode(existing.code);
      setClaiming(false);
      return;
    }

    const { data: available, error: fetchError } = await supabase
      .from("partner_codes")
      .select("id, code")
      .eq("brand", "RSM Store")
      .eq("campaign", "forever")
      .eq("status", "Available")
      .limit(1)
      .maybeSingle();

    if (!available || fetchError) {
      alert(`No more codes available.`);
      setClaiming(false);
      return;
    }

    const { error: updateError } = await supabase
      .from("partner_codes")
      .update({ 
        status: "Used", 
        assigned_to: session.user.id,
        claimed_at: new Date().toISOString()
      })
      .eq("id", available.id);

    if (updateError) {
      alert("Error claiming code.");
    } else {
      setClaimedCode(available.code);
    }
    setClaiming(false);
  };

  const closeModal = () => {
    setSelectedPartner(null);
    setShowCode(false);
    setClaimedCode(null);
    setCopied(null);
  };

  const handleCopy = (code, type) => {
    navigator.clipboard.writeText(code);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const filtered = partners.filter((p) => {
    if (tab !== "all" && p.type !== tab) return false;
    const q = query.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q);
  });

  const styles = {
    container: { minHeight: "100vh", backgroundColor: "#FFFFFF", padding: "1.5rem", paddingBottom: "120px", fontFamily: "'Inter', sans-serif", color: "#2D302D" },
    header: { fontSize: "2.4rem", fontWeight: "900", textAlign: "center", marginTop: "1rem", marginBottom: "2rem", letterSpacing: "-1.2px", background: "linear-gradient(135deg, #2D302D 0%, #12873B 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
    searchWrapper: { position: "relative", maxWidth: "480px", margin: "0 auto 1.5rem auto", width: "100%", display: "flex", alignItems: "center" },
    searchInput: { width: "100%", padding: "1.1rem 1rem 1.1rem 3.5rem", borderRadius: "18px", border: "1px solid #F0F2F0", backgroundColor: "#F7F9F7", fontSize: "1rem", outline: "none" },
    toggleContainer: { display: "flex", backgroundColor: "#D9DFD9", padding: "4px", borderRadius: "14px", marginBottom: "2rem", maxWidth: "480px", margin: "0 auto 2rem auto" },
    toggleBtn: (active) => ({ flex: 1, padding: "0.8rem", borderRadius: "10px", border: "none", background: active ? "linear-gradient(135deg, #2D302D 0%, #12873B 100%)" : "transparent", color: active ? "#FFFFFF" : "#6B7280", fontWeight: "700", fontSize: "0.85rem", cursor: "pointer" }),
    partnerCard: { width: "100%", borderRadius: "28px", overflow: "hidden", marginBottom: "2rem", cursor: "pointer", backgroundColor: "#F0F7F2", border: "1px solid rgba(18, 135, 59, 0.1)", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" },
    cardImageWrapper: (url) => ({ width: "100%", height: "180px", background: url ? `url(${url}) center/cover no-repeat` : PLACEHOLDER_BANNER, position: "relative" }),
    cardLogoOverlay: { position: "absolute", bottom: "12px", left: "16px", width: "50px", height: "50px", backgroundColor: "white", borderRadius: "14px", padding: "5px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 },
    cardBadge: { position: "absolute", top: "12px", right: "16px", backgroundColor: "#FFFFFF", padding: "6px 14px", borderRadius: "100px", fontSize: "0.75rem", fontWeight: "900", color: "#12873B", boxShadow: "0 4px 10px rgba(0,0,0,0.1)", zIndex: 2 },
    modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(12px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" },
    modalContent: { backgroundColor: "#FFFFFF", width: "100%", maxWidth: "420px", maxHeight: "90vh", overflowY: "auto", borderRadius: "32px", position: "relative", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.4)", scrollbarWidth: "none" },
    modalBanner: (url) => ({ width: "100%", height: "180px", background: url ? `url(${url}) center/cover no-repeat` : PLACEHOLDER_BANNER, position: "relative" }),
    gradientCloseBtn: { position: "absolute", top: "1rem", right: "1rem", background: "linear-gradient(135deg, #2D302D 0%, #12873B 100%)", border: "none", borderRadius: "50%", padding: "8px", cursor: "pointer", zIndex: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center" },
    footerCloseBtn: { width: "100%", padding: "1.1rem", borderRadius: "16px", border: "none", background: "linear-gradient(135deg, #2D302D 0%, #12873B 100%)", color: "white", fontWeight: "700", marginTop: "1.5rem", cursor: "pointer", boxShadow: "0 4px 12px rgba(18, 135, 59, 0.2)" }
  };

  return (
    <div style={styles.container}>
      <style>{`.modal-content::-webkit-scrollbar { display: none; } ${selectedPartner ? 'nav { display: none !important; }' : ''}`}</style>
      <h1 style={styles.header}>Partners</h1>
      <div style={{ maxWidth: "480px", margin: "0 auto", width: "100%" }}>
        <div style={styles.searchWrapper}>
          <Search size={20} style={{ position: "absolute", left: "1.2rem", color: "#94A3B8" }} />
          <input style={styles.searchInput} placeholder="Search stores..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div style={styles.toggleContainer}>
          <button onClick={() => setTab("all")} style={styles.toggleBtn(tab === "all")}>All</button>
          <button onClick={() => setTab("discount")} style={styles.toggleBtn(tab === "discount")}>Discounts</button>
          <button onClick={() => setTab("points")} style={styles.toggleBtn(tab === "points")}>Points</button>
        </div>

        {loading ? (
          <p style={{ textAlign: "center", color: "#B2BBB2" }}>Updating...</p>
        ) : (
          filtered.map((p) => (
            <div key={p.id} style={styles.partnerCard} onClick={() => setSelectedPartner(p)}>
              <div style={styles.cardImageWrapper(p.banner_picture)}>
                <div style={styles.cardBadge}>{p.type === "discount" ? p.discount : "Points"}</div>
                <div style={styles.cardLogoOverlay}>
                   <img src={p.logo_url || PLACEHOLDER_LOGO} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "10px" }} />
                </div>
              </div>
              <div style={{ padding: "18px 20px 22px 20px" }}>
                <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "900", color: "#2D302D" }}>{p.name}</h3>
                <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                  <p style={{ margin: 0, fontSize: "0.6rem", color: "#12873B", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {p.type} Partner
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedPartner && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div className="modal-content" style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalBanner(selectedPartner.banner_picture)}>
                <button onClick={closeModal} style={styles.gradientCloseBtn}><X size={20} color="#FFFFFF" /></button>
                <div style={{ width: "80px", height: "80px", backgroundColor: "white", borderRadius: "22px", padding: "6px", position: "absolute", bottom: "-40px", left: "1.5rem", boxShadow: "0 8px 20px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 }}>
                    <img src={selectedPartner.logo_url || PLACEHOLDER_LOGO} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "14px" }} alt="logo" />
                </div>
            </div>

            <div style={{ padding: "3.5rem 1.5rem 2rem 1.5rem" }}>
                <h2 style={{ margin: "0 0 0.5rem 0", fontWeight: "900", fontSize: "1.7rem" }}>{selectedPartner.name}</h2>
                <div style={{ display: "flex", gap: "1.5rem", margin: "1.2rem 0", padding: "14px 0", borderTop: "1px solid #F0F2F0", borderBottom: "1px solid #F0F2F0" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontSize: "0.65rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Your Advantage</span>
                        <span style={{ fontSize: "0.95rem", fontWeight: "800", color: "#12873B" }}>{selectedPartner.short || selectedPartner.discount}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontSize: "0.65rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Type</span>
                        <span style={{ fontSize: "0.95rem", fontWeight: "800", color: "#12873B", textTransform: "capitalize" }}>{selectedPartner.type}</span>
                    </div>
                </div>

                <p style={{ color: "#4A5568", marginBottom: "2rem", fontSize: "0.95rem", lineHeight: "1.6" }}>{selectedPartner.description}</p>

                {(selectedPartner.discount_code || selectedPartner.name === "RSM Store") && (
                    <div style={{ marginBottom: "1.5rem", width: "100%", display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
                      {hasCard === "yes" ? (
                        selectedPartner.name === "RSM Store" ? (
                            !claimedCode ? (
                              <button style={{ width: "100%", padding: "1.1rem", borderRadius: "16px", border: "none", background: "linear-gradient(135deg, #2D302D 0%, #12873B 100%)", color: "white", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", cursor: "pointer" }} onClick={handleRevealCode}>
                                <Ticket size={20} /> {claiming ? "Claiming..." : "Reveal Personal Code"}
                              </button>
                            ) : (
                              <div style={{ width: "100%", padding: "1.2rem", boxSizing: "border-box", borderRadius: "16px", backgroundColor: "#F0F4F1", border: "2px dashed #12873B", textAlign: "center", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", overflow: "hidden" }} onClick={() => handleCopy(claimedCode, "forever")}>
                                <span style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", marginBottom: "8px", color: "#12873B", opacity: 0.7 }}>
                                    {copied === "forever" ? "Copied!" : "Tap to copy"}
                                </span>
                                <div style={{ fontSize: "1.4rem", fontWeight: "900", color: "#12873B", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", width: "100%", wordBreak: "break-all" }}>
                                    {claimedCode}
                                    {copied === "forever" ? <Check size={20} /> : <Copy size={18} />}
                                </div>
                              </div>
                            )
                        ) : (
                          !showCode ? (
                            <button style={{ width: "100%", padding: "1.1rem", borderRadius: "16px", border: "none", background: "linear-gradient(135deg, #2D302D 0%, #12873B 100%)", color: "white", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", cursor: "pointer" }} onClick={() => setShowCode(true)}>
                                <Ticket size={20} /> Reveal Personal Code
                            </button>
                          ) : (
                            <div style={{ width: "100%", padding: "1.2rem", boxSizing: "border-box", borderRadius: "16px", backgroundColor: "#F0F4F1", border: "2px dashed #12873B", textAlign: "center", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", overflow: "hidden" }} onClick={() => handleCopy(selectedPartner.discount_code, "single")}>
                                <span style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", marginBottom: "8px", color: "#12873B", opacity: 0.7 }}>
                                    {copied === "single" ? "Copied!" : "Tap to copy"}
                                </span>
                                <div style={{ fontSize: "1.4rem", fontWeight: "900", color: "#12873B", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", width: "100%", wordBreak: "break-all" }}>
                                    {selectedPartner.discount_code}
                                    {copied === "single" ? <Check size={20} /> : <Copy size={18} />}
                                </div>
                            </div>
                          )
                        )
                      ) : (
                        <Link to="/get-card" style={{ width: "100%", padding: "1.1rem", borderRadius: "18px", border: "1.5px solid #E2E8F0", background: "#F8FAFB", color: "#12873B", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", gap: "10px", boxSizing: "border-box" }}>
                          <Lock size={20} color="#94A3B8" /> <span>Get Card to Unlock</span>
                        </Link>
                      )}
                    </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {selectedPartner.address && (
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px", backgroundColor: "#F7F9F7", borderRadius: "16px", fontSize: "0.9rem", fontWeight: "600" }}>
                        <MapPin size={20} color="#12873B" /> <span style={{ flex: 1 }}>{selectedPartner.address}</span>
                      </div>
                    )}
                    {(selectedPartner["website "] || selectedPartner.website) && (
                      <a href={selectedPartner["website "] || selectedPartner.website} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px", backgroundColor: "#F7F9F7", borderRadius: "16px", textDecoration: "none", color: "#2D302D", fontSize: "0.9rem", fontWeight: "600" }}>
                        <Globe size={20} color="#12873B" /> <span style={{ flex: 1 }}>Visit Website</span> <ExternalLink size={16} color="#94A3B8" />
                      </a>
                    )}
                </div>
                <button onClick={closeModal} style={styles.footerCloseBtn}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}